import { Component } from 'react';
import PropTypes from 'prop-types';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { View } from 'foundations-components/components/View';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { Tabs, Tab } from 'foundations-components/transitional/components/Tabs';
import { ACCESS_LEVELS } from '../constants/AccessLevels';
import { TeamOptionType, AccessLevelType, UserOptionType } from '../types/OptionTypes';
import { defaultDisallowedTeamFormattedMessage, getUpdatedSelectionOfUsersAndTeamsFromTeamAdd, getUpdatedSelectionOfUsersAndTeamsFromTeamRemove } from '../utils/TeamSelectionUtils';
import { getRootLevelTeamIds, getUserIdsForSelectedTeamIds } from '../utils/TeamUtils';
import UsersAndTeamsAccessStatus from './accessStatus/UsersAndTeamsAccessStatus';
import AssetAccessLevelPicker from './AssetAccessLevelPicker';
import UsersAndTeamsSearch from './search/UsersAndTeamsSearch';
import SelectableTeamsList from './SelectableTeamsList';
import SelectableUsersList from './SelectableUsersList';
import { getExplicitlySelectedUserIds } from '../utils/TeamUtils';
import { getUserId } from '../utils/UserUtils';
import { getCurrentAccessLevel } from '../utils/AssetAccessUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const USERS_TAB_ID = 'users-tab';
const TEAMS_TAB_ID = 'teams-tab';
const areArraysDifferent = (arr1, arr2) => {
  if (arr1.length !== arr2.length) {
    return true;
  }
  return !arr1.every(id => arr2.includes(id));
};
class UsersAndTeamsSelector extends Component {
  constructor(props) {
    super(props);
    this.handleAddTeam = (addedTeamId, cascadeSelection = true) => {
      if (!addedTeamId) return;
      const {
        selectedTeamIds,
        selectedUserIds
      } = getUpdatedSelectionOfUsersAndTeamsFromTeamAdd({
        addedTeamId,
        teams: this.props.teams,
        previouslySelectedTeamIds: this.state.selectedTeamIds,
        previouslySelectedUserIds: this.state.selectedUserIds,
        cascadeSelection
      });
      this.handleUpdateTeamOrUserIds({
        selectedUserIds,
        selectedTeamIds
      });
      this.setState({
        selectedUserIds,
        selectedTeamIds
      });
    };
    this.handleRemoveTeam = removedTeamId => {
      if (!removedTeamId) return;
      const {
        selectedTeamIds,
        selectedUserIds
      } = getUpdatedSelectionOfUsersAndTeamsFromTeamRemove({
        removedTeamId,
        teams: this.props.teams,
        previouslySelectedTeamIds: this.state.selectedTeamIds,
        previouslySelectedUserIds: this.state.selectedUserIds
      });
      this.handleUpdateTeamOrUserIds({
        selectedUserIds,
        selectedTeamIds
      });
      this.setState({
        selectedUserIds,
        selectedTeamIds
      });
    };
    this.handleAddUser = userId => {
      if (!userId) return;
      const {
        selectedUserIds,
        selectedTeamIds
      } = this.state;
      const updatedUserIds = selectedUserIds.concat(userId);
      this.handleUpdateTeamOrUserIds({
        selectedUserIds: updatedUserIds,
        selectedTeamIds
      });
      this.setState({
        selectedUserIds: updatedUserIds
      });
    };
    this.handleRemoveUser = userId => {
      if (!userId) return;
      const {
        selectedUserIds,
        selectedTeamIds
      } = this.state;
      const updatedUserIds = selectedUserIds.filter(id => id !== userId);
      this.handleUpdateTeamOrUserIds({
        selectedUserIds: updatedUserIds,
        selectedTeamIds
      });
      this.setState({
        selectedUserIds: updatedUserIds
      });
    };
    this.handleUpdateTeamOrUserIds = ({
      selectedAccessLevel = ACCESS_LEVELS.SELECTED_USERS_AND_TEAMS,
      selectedUserIds,
      selectedTeamIds
    }) => {
      // This runs the onUpdateUserIds and onUpdateTeamIds passed in
      // Allowing user to save with most recent data
      const {
        onUpdateUserIds,
        onUpdateTeamIds,
        teams
      } = this.props;
      if (selectedAccessLevel === ACCESS_LEVELS.SELECTED_USERS_AND_TEAMS) {
        onUpdateTeamIds(selectedTeamIds);
        onUpdateUserIds(getExplicitlySelectedUserIds(selectedUserIds, selectedTeamIds, teams));
      } else if (selectedAccessLevel === ACCESS_LEVELS.PRIVATE) {
        onUpdateTeamIds([]);
        onUpdateUserIds([getUserId()]);
      } else {
        onUpdateUserIds([]);
        onUpdateTeamIds([]);
      }
    };
    this.setSelectedAccessLevel = selectedAccessLevel => {
      const {
        onUpdateAccessLevel
      } = this.props;
      const {
        selectedUserIds,
        selectedTeamIds
      } = this.state;
      this.handleUpdateTeamOrUserIds({
        selectedAccessLevel,
        selectedUserIds,
        selectedTeamIds
      });
      onUpdateAccessLevel(selectedAccessLevel);
      this.setState({
        selectedAccessLevel
      });
    };
    const {
      assignedUserIds,
      assignedTeamIds,
      assignedAccessLevel,
      disableAccessToEveryone
    } = props;
    // If no users or teams are assigned, then the content is available to everyone by default,
    // otherwise default to selected users and teams.
    const _selectedAccessLevel = getCurrentAccessLevel({
      assignedUserIds,
      assignedTeamIds,
      assignedAccessLevel,
      disableAccessToEveryone
    });
    this.handleUpdateTeamOrUserIds({
      selectedAccessLevel: _selectedAccessLevel,
      selectedUserIds: assignedUserIds,
      selectedTeamIds: assignedTeamIds
    });
    this.state = {
      selectedAccessLevel: _selectedAccessLevel,
      selectedUserIds: assignedUserIds,
      selectedTeamIds: assignedTeamIds,
      selectedTab: TEAMS_TAB_ID
    };
  }
  componentDidUpdate(prevProps) {
    const {
      assignedUserIds,
      assignedTeamIds,
      requestsLoading,
      assignedAccessLevel
    } = this.props;
    // Allow component to be fully controlled by props passed in
    if (prevProps.requestsLoading !== requestsLoading || prevProps.assignedAccessLevel !== assignedAccessLevel || areArraysDifferent(prevProps.assignedUserIds, assignedUserIds) || areArraysDifferent(prevProps.assignedTeamIds, assignedTeamIds)) {
      const selectedAccessLevel = getCurrentAccessLevel(this.props);
      this.handleUpdateTeamOrUserIds({
        selectedAccessLevel,
        selectedUserIds: assignedUserIds,
        selectedTeamIds: assignedTeamIds
      });
      this.setState({
        selectedAccessLevel,
        selectedUserIds: assignedUserIds,
        selectedTeamIds: assignedTeamIds
      });
    }
  }
  renderUsersAndTeamsTabs() {
    const {
      teams,
      userHasTeamAssignmentScope,
      allowedTeamIds,
      disallowedTeamFormattedMessage,
      allowedUserIds,
      disallowedUserFormattedMessageKey
    } = this.props;
    const {
      selectedUserIds,
      selectedTeamIds,
      selectedTab
    } = this.state;
    const rootLevelTeamIds = getRootLevelTeamIds(teams);
    const userIdsForSelectedTeamIds = getUserIdsForSelectedTeamIds(selectedTeamIds, teams);
    return /*#__PURE__*/_jsxs(Tabs, {
      selected: selectedTab,
      onSelectedChange: ({
        target: {
          value
        }
      }) => {
        this.setState({
          selectedTab: value.toString()
        });
      },
      children: [/*#__PURE__*/_jsx(Tab, {
        "data-test-id": TEAMS_TAB_ID,
        tabId: TEAMS_TAB_ID,
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ui-asset-management-lib.labels.teams"
        }),
        children: /*#__PURE__*/_jsx(SelectableTeamsList, {
          teams: teams,
          selectedTeamIds: selectedTeamIds,
          rootLevelTeamIds: rootLevelTeamIds,
          onAddTeam: this.handleAddTeam,
          onRemoveTeam: this.handleRemoveTeam,
          userHasTeamAssignmentScope: userHasTeamAssignmentScope || false,
          allowedTeamIds: allowedTeamIds,
          DisallowedTeamFormattedMessage: disallowedTeamFormattedMessage
        })
      }), /*#__PURE__*/_jsx(Tab, {
        "data-test-id": USERS_TAB_ID,
        disabled: !userHasTeamAssignmentScope,
        tooltip: !userHasTeamAssignmentScope && /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ui-asset-management-lib.tooltips.noUserAccess"
        }),
        tabId: USERS_TAB_ID,
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ui-asset-management-lib.labels.users"
        }),
        children: /*#__PURE__*/_jsx(SelectableUsersList, {
          selectedUserIds: selectedUserIds,
          userIdsForSelectedTeamIds: userIdsForSelectedTeamIds,
          onAddUser: this.handleAddUser,
          onRemoveUser: this.handleRemoveUser,
          allowedUserIds: allowedUserIds,
          disallowedUserTooltip: disallowedUserFormattedMessageKey
        })
      })]
    });
  }
  renderBody() {
    const {
      selectedUserInfo,
      teams,
      requestsLoading,
      requestsError,
      userHasTeamAssignmentScope,
      isTeamsOnly,
      isUsersOnly,
      allowedTeamIds,
      disallowedTeamFormattedMessage,
      allowedUserIds,
      disallowedUserFormattedMessageKey
    } = this.props;
    const {
      selectedUserIds,
      selectedTeamIds,
      selectedAccessLevel
    } = this.state;
    if (requestsLoading) {
      return /*#__PURE__*/_jsx(LoadingSpinner, {
        grow: true
      });
    }
    if (requestsError) {
      return /*#__PURE__*/_jsx(ErrorMessage, {
        type: "badRequest",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ui-asset-management-lib.assetAccessPanel.errorMessage"
        })
      });
    }
    if (selectedAccessLevel === ACCESS_LEVELS.EVERYONE || selectedAccessLevel === ACCESS_LEVELS.PRIVATE) {
      return null;
    }
    const rootLevelTeamIds = getRootLevelTeamIds(teams);
    const userIdsForSelectedTeamIds = getUserIdsForSelectedTeamIds(selectedTeamIds, teams);
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(View, {
        as: "div",
        inlineSize: "100%",
        maxInlineSize: "35rem",
        children: /*#__PURE__*/_jsx(UsersAndTeamsAccessStatus, {
          selectedUserInfo: selectedUserInfo,
          teams: teams,
          selectedUserIds: selectedUserIds,
          selectedTeamIds: selectedTeamIds,
          userIdsForSelectedTeamIds: userIdsForSelectedTeamIds,
          onRemoveTeam: this.handleRemoveTeam,
          onRemoveUser: this.handleRemoveUser,
          rootLevelTeamIds: rootLevelTeamIds,
          userHasTeamAssignmentScope: userHasTeamAssignmentScope || false,
          allowedUserIds: allowedUserIds,
          disallowedUserTooltip: disallowedUserFormattedMessageKey
        })
      }), /*#__PURE__*/_jsxs(View, {
        as: "div",
        inlineSize: "100%",
        maxInlineSize: "35rem",
        children: [userHasTeamAssignmentScope && /*#__PURE__*/_jsx(UsersAndTeamsSearch, {
          teams: teams,
          selectedUserIds: selectedUserIds,
          selectedTeamIds: selectedTeamIds,
          userIdsForSelectedTeamIds: userIdsForSelectedTeamIds,
          onAddTeam: this.handleAddTeam,
          onAddUser: this.handleAddUser,
          onRemoveTeam: this.handleRemoveTeam,
          onRemoveUser: this.handleRemoveUser,
          allowedTeamIds: allowedTeamIds,
          DisallowedTeamFormattedMessage: disallowedTeamFormattedMessage,
          allowedUserIds: allowedUserIds,
          disallowedUserTooltip: disallowedUserFormattedMessageKey,
          isTeamsOnly: isTeamsOnly,
          isUsersOnly: isUsersOnly
        }), !isTeamsOnly && !isUsersOnly && this.renderUsersAndTeamsTabs(), !isUsersOnly && isTeamsOnly && /*#__PURE__*/_jsx(View, {
          marginBlockStart: "400",
          as: "div",
          children: /*#__PURE__*/_jsx(SelectableTeamsList, {
            teams: teams,
            selectedTeamIds: selectedTeamIds,
            rootLevelTeamIds: rootLevelTeamIds,
            onAddTeam: this.handleAddTeam,
            onRemoveTeam: this.handleRemoveTeam,
            userHasTeamAssignmentScope: userHasTeamAssignmentScope || false,
            allowedTeamIds: allowedTeamIds,
            DisallowedTeamFormattedMessage: disallowedTeamFormattedMessage
          })
        }), !isTeamsOnly && isUsersOnly && /*#__PURE__*/_jsx(View, {
          marginBlockStart: "400",
          as: "div",
          children: /*#__PURE__*/_jsx(SelectableUsersList, {
            selectedUserIds: selectedUserIds,
            onAddUser: this.handleAddUser,
            onRemoveUser: this.handleRemoveUser,
            allowedUserIds: allowedUserIds,
            disallowedUserTooltip: disallowedUserFormattedMessageKey,
            userIdsForSelectedTeamIds: userIdsForSelectedTeamIds
          })
        })]
      })]
    });
  }
  render() {
    const {
      allowPrivate,
      allowSetEditLevel,
      userHasTeamAssignmentScope,
      isTeamsOnly,
      isUsersOnly,
      baseAccessValue,
      useAccessLanguage,
      disableAccessToEveryone,
      disableAccessToEveryoneFormattedMessage,
      hideAccessLevelPicker
    } = this.props;
    const {
      selectedAccessLevel
    } = this.state;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [!hideAccessLevelPicker && /*#__PURE__*/_jsx(View, {
        as: "div",
        inlineSize: "100%",
        maxInlineSize: "35rem",
        marginBlockEnd: "400",
        children: /*#__PURE__*/_jsx(AssetAccessLevelPicker, {
          value: selectedAccessLevel,
          allowPrivate: allowPrivate || false,
          allowSetEditLevel: allowSetEditLevel || false,
          disabled: !userHasTeamAssignmentScope,
          onChange: this.setSelectedAccessLevel,
          isTeamsOnly: isTeamsOnly,
          isUsersOnly: isUsersOnly,
          baseAccessValue: baseAccessValue,
          useAccessLanguage: useAccessLanguage,
          disableAccessToEveryone: disableAccessToEveryone,
          DisableAccessToEveryoneFormattedMessage: disableAccessToEveryoneFormattedMessage
        })
      }), this.renderBody()]
    });
  }
}
UsersAndTeamsSelector.propTypes = {
  // If the user can assign the asset to just themselves "Private to me.", also requires a user to assign at least one team when assigning users to an asset
  allowPrivate: PropTypes.bool,
  // If the user can make the asset "view only", or set it to "view and edit" for everyone. Allowing to set the edit level includes the ability to set private (TODO: Is that right, I think its fair...)
  allowSetEditLevel: PropTypes.bool,
  // An optional array of User ID's that are currently assigned to the asset
  assignedUserIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  // An optional array of Team ID's that are currently assigned to the asset
  assignedTeamIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  // An optional string indicating current accessLevel of toggle set
  assignedAccessLevel: AccessLevelType,
  // An optional object that holds extra visual components to add to the selected user option
  selectedUserInfo: PropTypes.object,
  // An array of users in the portal
  /** @deprecated Component now handles calling users on its own */
  users: PropTypes.arrayOf(UserOptionType),
  // The team structure for the portal
  teams: PropTypes.arrayOf(TeamOptionType).isRequired,
  // Function called whenever userIds are updated - initially called and set with assignedUserIds passed in
  onUpdateUserIds: PropTypes.func.isRequired,
  // Function called whenever teamIds are updated - initially called and set with assignedTeamIds passed in
  onUpdateTeamIds: PropTypes.func.isRequired,
  // Function called whenever accessLevel is updated - initially called and set with assignedAccessLevel passed in
  onUpdateAccessLevel: PropTypes.func.isRequired,
  // Optional: A list of users IDs which are allowed to be selected
  allowedUserIds: PropTypes.arrayOf(PropTypes.number),
  // Optional: The formatted message key which is used in a formatted message, displayed within the tooltip on users which are not selectable because of the allowedUserIds.
  disallowedUserFormattedMessageKey: PropTypes.string,
  // Optional: A list of team ids which are allowed to be selected in the tree
  allowedTeamIds: PropTypes.arrayOf(PropTypes.number),
  // Optional: The formatted message which is displayed within the tooltip on teams which are not selectable because of the allowedTeamIds. This function needs to return a FormattedMessage.
  disallowedTeamFormattedMessage: PropTypes.func,
  // Optional: If requests for either users or teams are loading to show a loading state
  requestsLoading: PropTypes.bool,
  // Optional: If requests for either users or teams have failed show a failure state.
  requestsError: PropTypes.bool,
  // ** Legacy behavior support **
  // If a user does not have "content-team-assignment" scope, but is on a team that is assigned to an asset, and that team has children
  // The user can:
  // - assign/unassign any child teams of the team they are a member of.
  // The user cannot:
  // - View the full team hierarchy
  // - Unassign their own team
  // - Change the base level of assignment of the asset
  // - Assign/unassign individual users, or view which users may already be assigned
  userHasTeamAssignmentScope: PropTypes.bool,
  // Optional: styling prop set to true when used in `UsersAndTeamsAssetAccessPanel` - default false
  fullWidth: PropTypes.bool,
  // Optional: Don't show tabs, only teams
  isTeamsOnly: PropTypes.bool,
  // Optional: Don't show tabs, only users
  isUsersOnly: PropTypes.bool,
  // Base/default asset access level to pick for label
  baseAccessValue: PropTypes.oneOf(Object.keys(ACCESS_LEVELS)),
  // If wanting to use language "access" instead of "edit"
  useAccessLanguage: PropTypes.bool,
  // Optional: a boolean flag that disables the various "everyone" access levels when set - default false
  disableAccessToEveryone: PropTypes.bool,
  // Optional: The formatted message which is displayed within the "everyone" access levels are disabled. This function needs to return a FormattedMessage.
  disableAccessToEveryoneFormattedMessage: PropTypes.func
};
UsersAndTeamsSelector.defaultProps = {
  allowPrivate: false,
  allowSetEditLevel: false,
  requestsError: false,
  requestsLoading: false,
  userHasTeamAssignmentScope: true,
  fullWidth: false,
  // Required for use in `onUpdateAccessLevel`, but not used with `UsersAndTeamsAssetAccessPanel` or `TeamsAssetAccessPanel`
  // @ts-expect-error TS doesn't like that this is also declared above, but we need it above as well in order
  //    for other files to pick up that this property has a default. Must be something to do with functions
  onUpdateAccessLevel: () => {},
  allowedTeamIds: undefined,
  disallowedTeamFormattedMessage: defaultDisallowedTeamFormattedMessage,
  disallowedUserFormattedMessageKey: 'ui-asset-management-lib.tooltips.disallowedUserToolTip',
  useAccessLanguage: false
};
export default UsersAndTeamsSelector;